import type { TeamRole } from "@sebas/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";

export type TeamSummary = { id: string; name: string; slug: string; role: TeamRole; createdAt: string };
export type Member = {
  userId: string; role: TeamRole; joinedAt: string;
  displayName: string | null; avatarUrl: string | null;
};
export type Invite = { id: string; email: string; role: TeamRole; expiresAt: string; token: string };
export type ActivityRow = {
  id: string; action: string; entityType: string; entityId: string;
  createdAt: string; actorName: string | null;
};
export type Comment = {
  id: string; body: string; resolvedAt: string | null; createdAt: string;
  authorId: string; authorName: string | null;
};
export type Share = {
  userId: string;
  role: TeamRole;
  displayName: string | null;
  /** Null while they have not answered the invitation yet. */
  acceptedAt: string | null;
};

export function useTeams() {
  return useQuery({ queryKey: ["teams"], queryFn: () => api<{ teams: TeamSummary[] }>("/v1/teams") });
}

export function useTeam(id: string | undefined) {
  return useQuery({
    queryKey: ["teams", id],
    queryFn: () =>
      api<{ team: TeamSummary; members: Member[]; invites: Invite[]; role: TeamRole }>(`/v1/teams/${id}`),
    enabled: Boolean(id),
  });
}

export function useTeamActivity(id: string | undefined) {
  return useQuery({
    queryKey: ["teams", id, "activity"],
    queryFn: () => api<{ activity: ActivityRow[] }>(`/v1/teams/${id}/activity`),
    enabled: Boolean(id),
  });
}

export function useCreateTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      api<{ team: TeamSummary }>("/v1/teams", { method: "POST", body: JSON.stringify({ name }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["teams"] }),
  });
}

export function useInvite(teamId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; role: TeamRole }) =>
      api<{ invite: Invite; acceptUrl: string; emailed: boolean; notified: boolean }>(`/v1/teams/${teamId}/invites`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["teams", teamId] }),
  });
}

export function useSetMemberRole(teamId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: TeamRole }) =>
      api(`/v1/teams/${teamId}/members/${userId}`, { method: "PATCH", body: JSON.stringify({ role }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["teams", teamId] }),
  });
}

export function useRemoveMember(teamId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api<void>(`/v1/teams/${teamId}/members/${userId}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["teams", teamId] });
      qc.invalidateQueries({ queryKey: ["teams"] });
    },
  });
}

export function useComments(noteId: string | undefined) {
  return useQuery({
    queryKey: ["notes", noteId, "comments"],
    queryFn: () => api<{ comments: Comment[] }>(`/v1/notes/${noteId}/comments`),
    enabled: Boolean(noteId),
  });
}

export function useAddComment(noteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) =>
      api<{ comment: Comment }>(`/v1/notes/${noteId}/comments`, { method: "POST", body: JSON.stringify({ body }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notes", noteId, "comments"] }),
  });
}

export function useResolveComment(noteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, resolved }: { id: string; resolved: boolean }) =>
      api(`/v1/notes/${noteId}/comments/${id}`, { method: "PATCH", body: JSON.stringify({ resolved }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notes", noteId, "comments"] }),
  });
}

export function useDeleteComment(noteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/v1/notes/${noteId}/comments/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notes", noteId, "comments"] }),
  });
}

export function useShares(noteId: string | undefined) {
  return useQuery({
    queryKey: ["notes", noteId, "shares"],
    queryFn: () => api<{ shares: Share[] }>(`/v1/notes/${noteId}/shares`),
    enabled: Boolean(noteId),
  });
}

export function useSetShares(noteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (shares: { userId: string; role: TeamRole }[]) =>
      api(`/v1/notes/${noteId}/shares`, { method: "PUT", body: JSON.stringify({ shares }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notes", noteId, "shares"] }),
  });
}

export const lookupUser = (email: string) =>
  api<{ userId: string; displayName: string | null }>(`/v1/notes/lookup/${encodeURIComponent(email)}`);
